<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter, RouterLink } from 'vue-router';
import { useEventStore } from '../stores/events';
import { useCampaignStore } from '../stores/campaigns';
import { useLocationStore } from '../stores/locations';
import { getBaseCapacity, isVirtualEvent, VIRTUAL_LOCATION_NAME } from '../utils/eventFormat';
import { supabase } from '../lib/supabaseClient';
import { fetchAllPages } from '../utils/supabasePagination';
import { CalendarIcon, MapPinIcon, UsersIcon, BanknotesIcon, RocketLaunchIcon, PlusCircleIcon } from '@heroicons/vue/24/outline';
import ErrorBoundary from '../components/common/ErrorBoundary.vue';
import AsyncWrapper from '../components/common/AsyncWrapper.vue';
import HelpTooltip from '../components/common/HelpTooltip.vue';
import Modal from '../components/common/Modal.vue';

const router = useRouter();
const eventStore = useEventStore();
const campaignStore = useCampaignStore();
const locationStore = useLocationStore();

// State
const eventCapacityDetails = ref({});
const isLoading = ref(true);
const showEventDetails = ref(false);
const selectedEvent = ref(null);
const totalRevenue = ref(0);

// Computed properties
const upcomingEvents = computed(() => {
  const now = new Date();
  return eventStore.events
    .filter(event => new Date(event.date) >= now)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
});

const averageEventCost = computed(() => {
  const events = eventStore.events;
  if (!events.length) return 0;
  
  const totalCost = events.reduce((sum, event) => {
    const location = locationStore.locations.find(loc => loc.id === event.locationId);
    return sum + (location?.cost || 0);
  }, 0);
  
  return totalCost / events.length;
});

// Calculate real percentage changes
const campaignStats = computed(() => {
  const now = new Date();
  const campaigns = campaignStore.campaigns;
  const activeCampaigns = campaigns.filter(c => new Date(c.endDate) >= now);
  const totalCampaigns = campaigns.length;
  
  // Show percentage of campaigns that are active
  if (totalCampaigns === 0) return { count: 0, change: null, changeType: null };
  
  const activePercent = (activeCampaigns.length / totalCampaigns) * 100;
  return {
    count: activeCampaigns.length,
    change: `${activePercent.toFixed(0)}% active`,
    changeType: activePercent >= 50 ? 'positive' : 'neutral'
  };
});

const eventStats = computed(() => {
  const now = new Date();
  const events = eventStore.events;
  const upcomingCount = upcomingEvents.value.length;
  const pastEvents = events.filter(e => new Date(e.date) < now).length;
  
  // Show ratio of upcoming vs past events
  if (events.length === 0) return { count: 0, change: null, changeType: null };
  
  const upcomingPercent = (upcomingCount / events.length) * 100;
  return {
    count: upcomingCount,
    change: `${upcomingPercent.toFixed(0)}% upcoming`,
    changeType: upcomingPercent >= 30 ? 'positive' : 'neutral'
  };
});

const costStats = computed(() => {
  const events = eventStore.events;
  if (!events.length) return { value: formatCurrency(0), change: null, changeType: null };
  
  const avg = averageEventCost.value;
  
  return {
    value: formatCurrency(avg),
    change: null,
    changeType: null
  };
});

const totalCapacityFilled = computed(() => {
  // Calculate total registered vs total capacity for upcoming events
  let totalRegistered = 0;
  let totalCapacity = 0;
  
  for (const event of upcomingEvents.value) {
    const location = locationStore.locations.find(loc => loc.id === event.locationId);
    const baseCapacity = getBaseCapacity(event, location);
    const buffer = event.capacityBuffer || 0;
    totalCapacity += baseCapacity + buffer;
    
    const details = eventCapacityDetails.value[event.id];
    if (details) {
      totalRegistered += details.registered || 0;
    }
  }
  
  if (totalCapacity === 0) return null;
  return ((totalRegistered / totalCapacity) * 100).toFixed(0);
});

// Detect if this is a brand new user with zero data
const isNewUser = computed(() => {
  return eventStore.events.length === 0 && 
         campaignStore.campaigns.length === 0 && 
         locationStore.locations.length === 0;
});

const stats = computed(() => {
  const items = [
    {
      name: 'Active Campaigns',
      value: campaignStats.value.count,
      icon: CalendarIcon,
      change: campaignStats.value.change,
      changeType: campaignStats.value.changeType
    },
    {
      name: 'Total Locations',
      value: locationStore.locations.length,
      icon: MapPinIcon
    },
    {
      name: 'Upcoming Events',
      value: eventStats.value.count,
      icon: UsersIcon,
      change: totalCapacityFilled.value ? `${totalCapacityFilled.value}% filled` : eventStats.value.change,
      changeType: totalCapacityFilled.value 
        ? (parseInt(totalCapacityFilled.value) >= 50 ? 'positive' : 'neutral')
        : eventStats.value.changeType
    },
    {
      name: 'Average Event Cost',
      value: costStats.value.value,
      icon: BanknotesIcon,
      change: costStats.value.change,
      changeType: costStats.value.changeType
    }
  ];

  if (totalRevenue.value > 0) {
    items.push({
      name: 'Total Revenue',
      value: formatCurrency(totalRevenue.value),
      icon: BanknotesIcon,
      change: null,
      changeType: null
    });
  }

  return items;
});

// Add computed property for capacity display
const getCapacityDisplay = (event) => {
  const location = locationStore.locations.find(loc => loc.id === event.locationId);
  const baseCapacity = getBaseCapacity(event, location);
  const buffer = event.capacityBuffer || 0;
  const totalCapacity = baseCapacity + buffer;
  const details = eventCapacityDetails.value[event.id];
  
  if (!details) {
    return {
      display: `0 / ${totalCapacity}`,
      tooltip: 'Loading...',
      percentage: 0,
      capacityStatus: 'low'
    };
  }
  
  const registered = details.registered || 0;
  const withWaitlisted = details.registeredPlusWaitlistedGuests || 0;
  const hasWaitlistedGuests = registered !== withWaitlisted;
  const percentage = totalCapacity > 0 ? (registered / totalCapacity) * 100 : 0;
  
  // Only show middle number if there are waitlisted guests under registered hosts
  const display = hasWaitlistedGuests 
    ? `${registered} | ${withWaitlisted} / ${totalCapacity}`
    : `${registered} / ${totalCapacity}`;
  
  // Build tooltip with capacity breakdown
  const capacityBreakdown = buffer > 0 
    ? `${baseCapacity} base + ${buffer} buffer = ${totalCapacity} total capacity`
    : `${baseCapacity} capacity`;
  
  let tooltip = hasWaitlistedGuests
    ? `${registered} Registered (hosts + guests)\n${withWaitlisted} Including waitlisted guests under registered hosts\n${capacityBreakdown}`
    : `${registered} Registered (hosts + guests)\n${capacityBreakdown}`;
  
  // Add warning for buffer or over-capacity
  if (registered > totalCapacity) {
    tooltip += '\n⚠️ OVER CAPACITY';
  } else if (buffer > 0 && registered > baseCapacity) {
    tooltip += '\n⚠️ Using buffer capacity';
  }
  
  // Determine capacity status
  let capacityStatus;
  if (registered > totalCapacity) {
    capacityStatus = 'over'; // red - over total capacity
  } else if (buffer > 0 && registered > baseCapacity) {
    capacityStatus = 'atBuffer'; // orange - using buffer capacity
  } else if (registered === totalCapacity) {
    capacityStatus = 'full'; // green - exactly at capacity
  } else if (percentage >= 80) {
    capacityStatus = 'high'; // orange - approaching capacity
  } else {
    capacityStatus = 'low'; // blue - plenty of room
  }
  
  return {
    display,
    tooltip,
    percentage,
    capacityStatus
  };
}

// Add function to format location display
const getLocationDisplay = (event) => {
  if (isVirtualEvent(event)) return VIRTUAL_LOCATION_NAME;
  const location = locationStore.locations.find(loc => loc.id === event.locationId);
  if (!location) return 'Unknown';
  
  const name = location.name || '';
  const address = location.address || '';
  const zipCode = location.zipCode || '';
  
  // Format: "name, streetAddress - zip code"
  if (name && address && zipCode) {
    return `${name}, ${address} - ${zipCode}`;
  } else if (name && address) {
    return `${name}, ${address}`;
  } else if (name) {
    return name;
  }
  
  return 'Unknown';
};

// Add navigation mapping for stats
const statNavigation = {
  'Active Campaigns': '/campaigns',
  'Total Locations': '/locations',
  'Upcoming Events': '/events',
  'Average Event Cost': null,
  'Total Revenue': '/reports'
};

// Methods
async function loadCapacityDetails(eventIds) {
  if (!eventIds.length) return;
  
  try {
    // Use the optimized RPC function to get capacity details for all events at once
    const { data, error } = await supabase
      .rpc('get_event_capacity_details', { p_event_ids: eventIds });
    
    if (error) throw error;
    
    // Populate the capacity details map
    data.forEach(item => {
      eventCapacityDetails.value[item.event_id] = {
        registered: Number(item.registered),
        registeredPlusWaitlistedGuests: Number(item.registered_plus_waitlisted_guests),
        waitlistedGuestsUnderRegisteredHosts: Number(item.registered_plus_waitlisted_guests) - Number(item.registered)
      };
    });
  } catch (error) {
    console.error('Failed to load capacity details:', error);
    // Set defaults for all events
    eventIds.forEach(eventId => {
      eventCapacityDetails.value[eventId] = {
        registered: 0,
        registeredPlusWaitlistedGuests: 0,
        waitlistedGuestsUnderRegisteredHosts: 0
      };
    });
  }
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
}

function formatDate(date) {
  return new Date(date).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function handleEventClick(event) {
  router.push(`/events/${event.id}/roster`);
}

// Update the click handler for stats
function handleStatClick(statName) {
  const route = statNavigation[statName];
  if (route) {
    router.push(route);
  }
}

// Lifecycle hooks
onMounted(async () => {
  try {
    await Promise.all([
      eventStore.fetchEvents(),
      campaignStore.fetchCampaigns(),
      locationStore.fetchLocations()
    ]);

    // Load capacity details for all upcoming events in a single RPC call
    const upcomingEventIds = upcomingEvents.value.map(event => event.id);
    await loadCapacityDetails(upcomingEventIds);

    // Load total revenue (non-blocking). Paginate so we never silently
    // truncate at the 1000-row API cap on busy orgs.
    fetchAllPages((from, to) =>
      supabase
        .from('event_attendees')
        .select('revenue')
        .not('revenue', 'is', null)
        .eq('_deleted', false)
        .range(from, to)
    ).then(({ data }) => {
      if (data) {
        totalRevenue.value = data.reduce((sum, ea) => sum + Number(ea.revenue || 0), 0);
      }
    });
  } catch (error) {
    console.error('Failed to load dashboard data:', error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <ErrorBoundary>
    <div>
      <div class="flex items-center gap-2">
        <h1 class="text-3xl font-bold leading-tight tracking-tight text-gray-900">Dashboard</h1>
        <HelpTooltip text="Your dashboard shows key stats and upcoming events at a glance. Click on any stat card to navigate to that section." />
      </div>
      
      <AsyncWrapper :loading="isLoading" :error="eventStore.error" :loading-text="'Loading dashboard data...'">
        
        <!-- New User Welcome / Onboarding Empty State -->
        <div v-if="isNewUser" class="mt-8">
          <div class="rounded-xl bg-gradient-to-br from-primary-800/5 via-action-50 to-action-100 border border-primary-800/10 p-8 text-center">
            <RocketLaunchIcon class="mx-auto h-16 w-16 text-primary-800/60" />
            <h2 class="mt-4 text-2xl font-bold text-gray-900">Welcome to EventFlow!</h2>
            <p class="mt-2 text-gray-600 max-w-lg mx-auto">
              Get started by setting up the building blocks for your events. Follow the steps below to create your first campaign.
            </p>
          </div>

          <div class="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <!-- Step 1: Add a Location -->
            <div 
              class="relative rounded-lg bg-white shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all cursor-pointer group"
              @click="router.push('/locations')"
            >
              <div class="flex items-center gap-3 mb-3">
                <span class="flex h-8 w-8 items-center justify-center rounded-full bg-primary-800/10 text-primary-800 font-bold text-sm">1</span>
                <h3 class="font-semibold text-gray-900">Add a Location</h3>
              </div>
              <p class="text-sm text-gray-500">Set up your event venues with capacity and address details.</p>
              <div class="mt-4 flex items-center text-sm font-medium text-action-600 group-hover:underline">
                <PlusCircleIcon class="h-4 w-4 mr-1" />
                Add Location
              </div>
            </div>

            <!-- Step 2: Create a Campaign -->
            <div 
              class="relative rounded-lg bg-white shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all cursor-pointer group"
              @click="router.push('/campaigns')"
            >
              <div class="flex items-center gap-3 mb-3">
                <span class="flex h-8 w-8 items-center justify-center rounded-full bg-primary-800/10 text-primary-800 font-bold text-sm">2</span>
                <h3 class="font-semibold text-gray-900">Create a Campaign</h3>
              </div>
              <p class="text-sm text-gray-500">Campaigns group related events for tracking and budget management.</p>
              <div class="mt-4 flex items-center text-sm font-medium text-action-600 group-hover:underline">
                <PlusCircleIcon class="h-4 w-4 mr-1" />
                Create Campaign
              </div>
            </div>

            <!-- Step 3: Schedule Events -->
            <div 
              class="relative rounded-lg bg-white shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all cursor-pointer group"
              @click="router.push('/events')"
            >
              <div class="flex items-center gap-3 mb-3">
                <span class="flex h-8 w-8 items-center justify-center rounded-full bg-primary-800/10 text-primary-800 font-bold text-sm">3</span>
                <h3 class="font-semibold text-gray-900">Schedule Events</h3>
              </div>
              <p class="text-sm text-gray-500">Create events within campaigns, assign presenters, and open registration.</p>
              <div class="mt-4 flex items-center text-sm font-medium text-action-600 group-hover:underline">
                <PlusCircleIcon class="h-4 w-4 mr-1" />
                Schedule Event
              </div>
            </div>
          </div>

          <!-- Quick Links -->
          <div class="mt-6 rounded-lg bg-white shadow-sm border border-gray-200 p-5">
            <h3 class="text-sm font-semibold text-gray-700 mb-3">Also consider setting up:</h3>
            <div class="flex flex-wrap gap-3">
              <button 
                @click="router.push('/configuration')"
                class="inline-flex items-center rounded-md bg-gray-100 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Presenters & Calendars
              </button>
              <button 
                @click="router.push('/communication')"
                class="inline-flex items-center rounded-md bg-gray-100 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Message Templates
              </button>
              <button 
                @click="router.push('/configuration')"
                class="inline-flex items-center rounded-md bg-gray-100 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Organization Settings
              </button>
            </div>
          </div>
        </div>

        <!-- Stats (shown when data exists) -->
        <div v-if="!isNewUser" class="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2" :class="stats.length > 4 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'">
          <div 
            v-for="stat in stats" 
            :key="stat.name" 
            :class="[
              'relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6',
              statNavigation[stat.name] ? 'cursor-pointer hover:shadow-md transition-all duration-200 hover:-translate-y-0.5' : ''
            ]"
            @click="handleStatClick(stat.name)"
          >
            <dt>
              <div class="absolute rounded-md bg-primary-800 p-3">
                <component :is="stat.icon" class="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              <p class="ml-16 truncate text-sm font-medium text-gray-500">{{ stat.name }}</p>
            </dt>
            <dd class="ml-16 flex items-baseline pb-6 sm:pb-7">
              <p class="text-2xl font-semibold text-gray-900">{{ stat.value }}</p>
              <p v-if="stat.change" :class="[
                  stat.changeType === 'positive' ? 'text-success-600' : 
                  stat.changeType === 'negative' ? 'text-danger-600' : 
                  'text-gray-500',
                  'ml-2 flex items-baseline text-sm font-semibold'
                ]">
                {{ stat.change }}
              </p>
            </dd>
            <div v-if="statNavigation[stat.name]" class="absolute bottom-0 left-0 right-0 h-1 bg-action-600 opacity-0 transition-opacity duration-200 group-hover:opacity-100"></div>
          </div>
        </div>

        <!-- Upcoming Events (shown when data exists) -->
        <div v-if="!isNewUser" class="mt-8">
          <h2 class="text-lg font-medium text-gray-900">Upcoming Events</h2>
          <div class="mt-4 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <table class="min-w-full divide-y divide-gray-300">
              <thead class="bg-gray-50">
                <tr>
                  <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Event</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Presenter</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Location</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Capacity</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr v-if="upcomingEvents.length === 0">
                  <td colspan="5" class="py-8 text-center text-sm text-gray-500">
                    <p class="font-medium text-gray-900">No upcoming events</p>
                    <p class="mt-1">All events are in the past, or none have been created yet.</p>
                    <button 
                      @click="router.push('/events')"
                      class="mt-3 inline-flex items-center rounded-md bg-action-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-action-700"
                    >
                      View All Events
                    </button>
                  </td>
                </tr>
                <tr 
                  v-for="event in upcomingEvents" 
                  :key="event.id"
                  class="hover:bg-gray-50 transition-colors duration-150"
                >
                  <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium sm:pl-6">
                    <RouterLink 
                      :to="`/events/${event.id}/roster`"
                      class="text-action-600 hover:text-action-700 hover:underline"
                    >
                      {{ event.name }}
                    </RouterLink>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ formatDate(event.date) }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ event.presenter || 'N/A' }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ getLocationDisplay(event) }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm">
                    <div class="flex items-center">
                      <span 
                        :class="[
                          'font-medium cursor-help',
                          getCapacityDisplay(event).capacityStatus === 'over' ? 'text-danger-600' :
                          getCapacityDisplay(event).capacityStatus === 'atBuffer' ? 'text-warning-600' :
                          getCapacityDisplay(event).capacityStatus === 'full' ? 'text-success-600' :
                          getCapacityDisplay(event).capacityStatus === 'high' ? 'text-warning-600' :
                          'text-gray-500'
                        ]"
                        :title="getCapacityDisplay(event).tooltip"
                      >
                        {{ getCapacityDisplay(event).display }}
                      </span>
                      <div class="ml-2 w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          class="h-2 rounded-full transition-all duration-300"
                          :class="[
                            getCapacityDisplay(event).capacityStatus === 'over' ? 'bg-danger-600' :
                            getCapacityDisplay(event).capacityStatus === 'atBuffer' ? 'bg-warning-500' :
                            getCapacityDisplay(event).capacityStatus === 'full' ? 'bg-success-600' :
                            getCapacityDisplay(event).capacityStatus === 'high' ? 'bg-warning-500' :
                            'bg-action-600'
                          ]"
                          :style="{ width: `${Math.min(getCapacityDisplay(event).percentage, 100)}%` }"
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </AsyncWrapper>
    </div>
  </ErrorBoundary>
</template>